// ── Menu registry (plain data mirror of Layout's NAV_GROUPS, no icons) ────────────────
// Single source used by the Access Control page + effective-access computation.
// KEEP IN SYNC with NAV_GROUPS in components/Layout.jsx (paths, codes, admin/superAdmin flags).
import { canSeeGroup, canSeeItem, SETUP_ITEM_ACCESS } from "./rbac";

export const MENU_REGISTRY = [
  { code: "DASH", label: "Dashboard", type: "link", to: "/", items: [] },
  { code: "FCT", label: "Forecasts", type: "menu", items: [
    { to: "/forecasts/walmart/overview", label: "Walmart · Overview" },
    { to: "/forecasts/walmart/order-view", label: "Walmart · Order View" },
    { to: "/forecasts/legrand/overview", label: "Legrand (WM-DI) · Overview" },
    { to: "/forecasts/legrand/order-view", label: "Legrand (WM-DI) · Order View" },
    { to: "/forecasts/other/new", label: "Other Customers · New Forecast" },
    { to: "/forecasts/other/view", label: "Other Customers · View Forecast" },
  ] },
  { code: "ORD", label: "Orders", type: "menu", items: [
    { to: "/intake-review", label: "PO Intake Review" },
    { to: "/pos", label: "POs" },
    { to: "/intake-audit", label: "Intake Audit Log" },
    { to: "/open-orders", label: "Open Orders" },
    { to: "/failures", label: "PO Intake Failures", admin: true },
    { to: "/po-reviews", label: "PO Reviews", admin: true },
    { to: "/sappush-soap", label: "Create SAP SO", admin: true },
  ] },
  { code: "DSP", label: "Dispatch", type: "menu", items: [
    { to: "/dispatch-register", label: "Dispatch Register" },
    { to: "/dispatch-plan", label: "Create Dispatch Plan" },
    { to: "/dispatch-plans", label: "Dispatch Plans" },
    { to: "/cfs-cutoff", label: "CFS Cut-Off" },
    { to: "/shipping", label: "Shipping" },
    { to: "/customer-status-report", label: "Customer open order status report" },
    { to: "/other-charges", label: "Other Charges & Invoices" },
  ] },
  { code: "LOG", label: "Logistics", type: "menu", items: [
    { to: "/logistics", label: "Capture Shipment Costs" },
    { to: "/logistics/analysis", label: "Cost Analysis" },
    { to: "/logistics/shipping-bills", label: "Upload Shipping Bill" },
    { to: "/logistics/shipping-bills/analytics", label: "Shipping Bill Analytics" },
    { to: "/logistics/container-loading", label: "Container Loading Photos" },
  ] },
  { code: "FAL", label: "Financial Receipts", type: "menu", items: [
    { to: "/fa-logistics", label: "Finance Receiving" },
    { to: "/fa-logistics/bulk-upload", label: "Bulk Receipt Upload" },
    { to: "/fa-logistics/receipts-from-advice", label: "Receipts from Advice" },
    { to: "/fa-logistics/sap-post-audit", label: "SAP Post Audit" },
  ] },
  { code: "INS", label: "Insights", type: "menu", items: [
    { to: "/otd-risk", label: "OTD & Risk" },
    { to: "/reports", label: "Open Order Report" },
    { to: "/explorer", label: "Open Order Explorer" },
    { to: "/ship-window", label: "Ask" },
    { to: "/sales", label: "Monthly Sales" },
    { to: "/sales-report", label: "Sales Report" },
    { to: "/short-closed", label: "Short-Closed POs" },
  ] },
  { code: "SET", label: "Setup", type: "menu", items: [
    { to: "/cockpit", label: "Admin Cockpit", superAdmin: true },
    { to: "/adminx", label: "Admin X", superAdmin: true },
    { to: "/data-health", label: "Data Health", admin: true },
    { to: "/rmp-lookup", label: "RMP Code Lookup", admin: true },
    { to: "/transfer-pricing", label: "Transfer Pricing" },
    { to: "/customers", label: "Business Config" },
    { to: "/train", label: "PO Training", admin: true },
    { to: "/import", label: "Excel Import", admin: true },
    { to: "/portal-push", label: "Portal Push", superAdmin: true },
    { to: "/reconcile", label: "ERP Reconcile", superAdmin: true },
    { to: "/inbox", label: "Adhoc Entry", superAdmin: true },
    { to: "/sap-mapping", label: "SAP Mapping", admin: true },
    { to: "/sap/outbound", label: "SAP Outbound", admin: true },
    { to: "/sap/dispatch-test", label: "SAP Dispatch Test", admin: true },
    { to: "/sap/invoicing", label: "SAP Invoicing", admin: true },
    { to: "/sap/customer-addresses", label: "SAP Customer Addresses", admin: true },
    { to: "/sap/address-map", label: "Overall Address Map", admin: true },
    { to: "/sap-credentials", label: "SAP Credentials", admin: true },
    { to: "/sap-health", label: "SAP Health", admin: true },
    { to: "/users", label: "User Admin", admin: true },
    { to: "/access-control", label: "Access Control", admin: true },
    { to: "/settings", label: "Settings", superAdmin: true },
  ] },
];

export const CONFIGURABLE_ROLES = ["admin", "manager", "logistics", "po_dispatch", "approver", "trainer", "user", "finance"];

// Default (current-behaviour) access for a role — used to pre-fill the matrix + as runtime fallback
// until a role is explicitly configured. Mirrors the legacy rbac.js semantics.
export function defaultAccessForRole(role) {
  const groups = [], items = [];
  // Admin default = everything EXCEPT super-admin-only items (preserves prior admin behaviour).
  if (role === "admin") {
    for (const g of MENU_REGISTRY) {
      groups.push(g.code);
      if (g.type === "link") { items.push(g.to); continue; }
      for (const it of g.items) if (!it.superAdmin) items.push(it.to);
    }
    return { groups, items };
  }
  // Finance default = Dashboard + the Financial Receipts menu.
  if (role === "finance") {
    return { groups: ["DASH", "FAL"], items: ["/", "/fa-logistics", "/fa-logistics/bulk-upload", "/fa-logistics/receipts-from-advice", "/fa-logistics/sap-post-audit"] };
  }
  for (const g of MENU_REGISTRY) {
    if (!canSeeGroup(role, g.code)) continue;
    groups.push(g.code);
    if (g.type === "link") { items.push(g.to); continue; }
    for (const it of g.items) {
      let ok;
      if (g.code === "SET" && SETUP_ITEM_ACCESS[role]) ok = SETUP_ITEM_ACCESS[role].includes(it.to);
      else ok = !it.admin && !it.superAdmin && canSeeItem(role, it.to);
      if (ok) items.push(it.to);
    }
  }
  return { groups, items };
}

// Longest-match the pathname to a registry item; returns {code, to} or null (utility route).
export function matchItem(pathname) {
  let best = null;
  for (const g of MENU_REGISTRY) {
    if (g.type === "link" && g.to === pathname) return { code: g.code, to: g.to };
    for (const it of (g.items || [])) {
      if (pathname === it.to || pathname.startsWith(it.to + "/")) {
        if (!best || it.to.length > best.to.length) best = { code: g.code, to: it.to };
      }
    }
  }
  return best;
}

// ── Runtime cache of the current user's effective access (fetched once by Layout) ──────
let MY_ACCESS = null; // { full, admin, configured, groups:[], items:[] } or null
export function setMyAccess(a) { MY_ACCESS = a; }
export function getMyAccess() { return MY_ACCESS; }

// Resolve the effective {full, admin, groups:Set, items:Set} for a role given the fetched config.
export function effective(role, my) {
  if (my && my.full) return { full: true, admin: !!my.admin };
  const cfg = (my && my.configured) ? { groups: my.groups, items: my.items } : defaultAccessForRole(role);
  return { full: false, admin: false, groups: new Set(cfg.groups), items: new Set(cfg.items) };
}
